import { useState, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { LuCheck, LuClock, LuExternalLink, LuSend, LuUndo2, LuX, LuTriangleAlert } from 'react-icons/lu';
import aviso, { textoDoErro } from '../lib/aviso';
import AssistenteService, { avisarAcaoDoAssistente, type AcaoAssistente } from '../services/AssistenteService';
import './AssistenteAcao.css';

const hora = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '';

/**
 * O cartão de uma ação do assistente, dentro da resposta.
 *
 * O cartão é o registro da ação na conversa: muda de estado no lugar e nunca
 * some. Proposta espera o clique (o modelo não confirma sozinho); feita oferece
 * desfazer quando o servidor diz que dá. Desenho fechado em
 * `docs/propostas/assistente-que-age-fase-1.html`, seção 4.
 */
const AssistenteAcao: FC<{
    acao: AcaoAssistente;
    onMudou: (acao: AcaoAssistente) => void;
    /** Fecha o painel ao abrir o registro — na tela cheia não há painel. */
    onAbrir?: () => void;
}> = ({ acao, onMudou, onAbrir }) => {
    const navigate = useNavigate();
    const [ocupado, setOcupado] = useState(false);

    /* A proposta vence no servidor; aqui só se esconde o botão que já não
       adiantaria. O estado oficial chega no próximo carregamento. */
    const vencida = acao.estado === 'proposta' && !!acao.expiraEm && new Date(acao.expiraEm) < new Date();
    const estado = vencida ? 'expirada' : acao.estado;

    /* "Avisar a equipe Brik" (20/10): o único cartão que o Brik AI oferece sem
       a pessoa pedir. Os botões dizem o que acontece, e a validade de 24 h não
       cabe num "vale até 14:32" sem a data. */
    const avisoEquipe = acao.tipo === 'sugestao.enviar';

    const executar = async (fazer: (id: number) => Promise<AcaoAssistente>, falha: string) => {
        setOcupado(true);
        try {
            const nova = await fazer(acao.acaoId);
            onMudou(nova);
            if (nova.estado === 'feita' || nova.estado === 'desfeita') avisarAcaoDoAssistente(nova);
        } catch (erro) {
            aviso.erro(textoDoErro(erro, falha));
        } finally {
            setOcupado(false);
        }
    };

    const rotuloEstado = () => {
        switch (estado) {
            case 'proposta': return <><LuClock size={13} /> Precisa da sua confirmação</>;
            case 'feita': return <><LuCheck size={13} /> {avisoEquipe ? 'Enviado à equipe Brik' : 'Feito pelo Brik AI'} às {hora(acao.atualizadaEm)}</>;
            case 'cancelada': return <><LuX size={13} /> {avisoEquipe ? 'Não enviado' : 'Cancelada'}</>;
            case 'expirada': return <><LuClock size={13} /> Expirou{acao.expiraEm ? ` às ${hora(acao.expiraEm)}` : ''}</>;
            case 'desfeita': return <><LuUndo2 size={13} /> Desfeita às {hora(acao.atualizadaEm)}</>;
            case 'falhou': return <><LuTriangleAlert size={13} /> Não foi feito</>;
        }
    };

    const abrir = () => {
        if (!acao.rota) return;
        onAbrir?.();
        navigate(acao.rota);
    };

    return (
        <div className={`agt-ac is-${estado}`}>
            <div className="agt-ac-k">{rotuloEstado()}</div>
            <div className="agt-ac-t">{acao.titulo}</div>

            {acao.linhas.length > 0 && (
                <div className="agt-ac-l">
                    {acao.linhas.map((l, i) => (
                        <div key={i} className="agt-ac-li">
                            <span className="agt-ac-r">{l.rotulo}</span>
                            <span className={estado === 'desfeita' ? 'is-riscado' : ''}>
                                {l.de && estado !== 'feita' && estado !== 'desfeita' && (
                                    <>{l.de}<span className="agt-ac-seta">→</span></>
                                )}
                                {l.de && estado !== 'feita' && estado !== 'desfeita' ? <b>{l.para}</b> : l.para}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {estado === 'falhou' && acao.erro && <div className="agt-ac-erro">{acao.erro}</div>}
            {estado === 'expirada' && <div className="agt-ac-nota">Nada foi feito. Peça de novo para refazer.</div>}
            {estado === 'proposta' && acao.semDesfazer && (
                <div className="agt-ac-nota">Depois de confirmar, isto não tem desfazer.</div>
            )}
            {avisoEquipe && estado === 'proposta' && (
                <div className="agt-ac-nota">Vai junto a sua pergunta, como você escreveu.</div>
            )}
            {avisoEquipe && estado === 'feita' && (
                <div className="agt-ac-nota">Se a equipe responder, chega no seu e-mail.</div>
            )}

            {estado === 'proposta' && avisoEquipe && (
                <div className="agt-ac-f">
                    <button type="button" className="agt-ac-b is-prim" disabled={ocupado}
                        onClick={() => executar(AssistenteService.confirmarAcao, 'Não foi possível avisar a equipe.')}>
                        <LuSend size={14} /> Enviar à equipe
                    </button>
                    <button type="button" className="agt-ac-b is-link" disabled={ocupado}
                        onClick={() => executar(AssistenteService.cancelarAcao, 'Não foi possível cancelar.')}>
                        Agora não
                    </button>
                </div>
            )}

            {estado === 'proposta' && !avisoEquipe && (
                <div className="agt-ac-f">
                    <button type="button" className="agt-ac-b is-prim" disabled={ocupado}
                        onClick={() => executar(AssistenteService.confirmarAcao, 'Não foi possível confirmar.')}>
                        <LuCheck size={14} /> Confirmar
                    </button>
                    <button type="button" className="agt-ac-b" disabled={ocupado}
                        onClick={() => executar(AssistenteService.cancelarAcao, 'Não foi possível cancelar.')}>
                        Cancelar
                    </button>
                    {acao.expiraEm && <span className="agt-ac-val">vale até {hora(acao.expiraEm)}</span>}
                </div>
            )}

            {estado === 'feita' && (acao.rota || acao.podeDesfazer) && (
                <div className="agt-ac-f">
                    {acao.rota && (
                        <button type="button" className="agt-ac-b is-link" onClick={abrir}>
                            <LuExternalLink size={14} /> Abrir
                        </button>
                    )}
                    {acao.podeDesfazer && (
                        <button type="button" className="agt-ac-b" disabled={ocupado}
                            onClick={() => executar(AssistenteService.desfazerAcao, 'Não foi possível desfazer.')}>
                            <LuUndo2 size={14} /> Desfazer
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default AssistenteAcao;
